import { ApiError, uuid } from "@gjwtrack/client";
import { ALL_PERMISSION_BITS, PERMISSION_INFO, Permission, type AdminBundle, type AdminOverview, type AdminRole } from "@gjwtrack/domain";
import type { FieldKind } from "@gjwtrack/query-language";
import { ArrowDown, ArrowUp, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { Centered } from "../App.tsx";
import { TopBar, type Crumb } from "../components/TopBar.tsx";
import { IntegrationsPage, WorkflowPage, WorkflowsPage } from "./WorkflowsPage.tsx";
import { ProjectSettings, projectTabLabel } from "./admin/ProjectSettings.tsx";
import { client, useClient } from "../lib/client.ts";
import { Button, Checkbox, Input, InputSize, Tag } from "../ui/ring.ts";
import { SimpleSelect } from "../ui/SimpleSelect.tsx";

/**
 * Administration: projects, fields & option sets, users, groups. Reads one
 * overview, runs commands, re-reads. Not live: admin pages are rarely open
 * by two people at once and a stale view just gets a 4xx toast.
 *
 * `/admin/projects/:id/*` is the project's own settings (YouTrack's four pages)
 * and replaces the administration nav with the project's own left column.
 */
export function AdminPage() {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  /**
   * The overview is read straight from the tables, so it is current the moment a command returns.
   * The schema snapshot behind `/api/meta` is not: it reloads from the event stream a few tens of
   * ms later, so it is read again shortly after (the same reason `client.createTag` polls it).
   */
  const reload = useCallback(async (): Promise<void> => {
    try {
      setData(await client.http.adminOverview());
      await client.reloadMeta();
      for (const ms of [200, 600]) setTimeout(() => void client.reloadMeta().catch(() => undefined), ms);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 403 ? "Administrators and project admins only." : (e as Error).message);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);

  /** Run a command, then refresh. Errors go to the toast list. */
  const run = async (name: string, input: unknown): Promise<boolean> => {
    try {
      await client.http.command(name, input, uuid());
      await reload();
      return true;
    } catch (e) {
      client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
      return false;
    }
  };

  // The project settings pages take the whole row: their own sidebar, their own breadcrumbs.
  const inProject = /^\/admin\/projects\/(\d+)(?:\/([a-z]+))?/.exec(pathname);
  const project = inProject ? data?.projects.find((p) => p.id === Number(inProject[1])) : undefined;
  const crumbs: Crumb[] = project
    ? [{ label: "Projects", to: "/admin/projects" }, { label: project.name, to: `/admin/projects/${project.id}` }, { label: "Settings" }, { label: projectTabLabel(inProject?.[2] ?? "general") }]
    : [{ label: "Administration" }];

  return (
    <div className="flex h-full flex-col">
      <TopBar crumbs={crumbs} query="" onQuery={(q) => navigate(q ? `/issues?q=${encodeURIComponent(q)}` : "/issues")} onCreate={() => navigate("/issues")} />
      <div className="flex min-h-0 flex-1">
        {error ? (
          <Centered>{error}</Centered>
        ) : !data ? (
          <Centered>Loading…</Centered>
        ) : inProject ? (
          <Routes>
            <Route path="projects/:id/*" element={<ProjectSettings data={data} run={run} reload={reload} />} />
          </Routes>
        ) : (
          <>
            <aside className="w-56 shrink-0 border-r border-line py-2">
              {[
                ["Projects", "/admin/projects"],
                ["Custom Fields", "/admin/fields"],
                ["Users", "/admin/users"],
                ["Groups", "/admin/groups"],
                ["Roles", "/admin/roles"],
                ["Workflows", "/admin/workflows"],
                ["Integrations", "/admin/integrations"],
              ].map(([label, to]) => (
                <Link key={to} to={to!} className={`nav-item mx-2 ${pathname.startsWith(to!) ? "active" : ""}`}>
                  {label}
                </Link>
              ))}
            </aside>
            <div className="min-w-0 flex-1 overflow-auto px-8 py-5">
              <Routes>
                <Route path="/" element={<Navigate to="/admin/projects" replace />} />
                <Route path="projects" element={<Projects data={data} run={run} />} />
                <Route path="fields" element={<Fields data={data} run={run} />} />
                <Route path="users" element={<Users data={data} run={run} />} />
                <Route path="groups" element={<Groups data={data} run={run} />} />
                <Route path="roles" element={<Roles data={data} run={run} />} />
                <Route path="workflows" element={<WorkflowsPage />} />
                <Route path="workflows/:id" element={<WorkflowPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
              </Routes>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

type Run = (name: string, input: unknown) => Promise<boolean>;

function H({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
      <div className="flex-1" />
      {action}
    </div>
  );
}

const Th = ({ children }: { children?: React.ReactNode }) => <th className="border-b border-line px-3 py-1.5 text-left text-xs font-medium text-muted uppercase">{children}</th>;
const Td = ({ children, className = "" }: { children?: React.ReactNode; className?: string }) => <td className={`border-b border-line px-3 py-1.5 ${className}`}>{children}</td>;

// ----------------------------------------------------------------- projects

function Projects({ data, run }: { data: AdminOverview; run: Run }) {
  const me = useClient((s) => s.me);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const create = async (): Promise<void> => {
    if (await run("createProject", { key: key.trim(), name: name.trim() })) {
      setKey("");
      setName("");
    }
  };
  return (
    <div>
      <H title="Projects" />
      <table className="w-full max-w-4xl">
        <thead>
          <tr>
            <Th>Key</Th>
            <Th>Name</Th>
            <Th>Members</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {data.projects.map((p) => (
            <tr key={p.id} className={p.archived ? "text-muted" : ""}>
              <Td className="font-mono">{p.key}</Td>
              <Td>
                <Link to={`/admin/projects/${p.id}/general`} className="text-value hover:underline">
                  {p.name}
                </Link>
                {p.archived && <span className="chip ml-2">archived</span>}
              </Td>
              <Td>{data.grants.filter((g) => g.projectId === p.id).length}</Td>
              <Td>
                {me?.isAdmin && (
                  <Button inline onClick={() => void run("archiveProject", { projectId: p.id, archived: !p.archived })}>
                    {p.archived ? "Restore" : "Archive"}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      {me?.isAdmin && (
        <form
          className="mt-6 flex max-w-4xl items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <div className="w-32">
            <Input label="Key" size={InputSize.FULL} inputClassName="font-mono" value={key} onChange={(e) => setKey(e.target.value)} placeholder="WEB" required />
          </div>
          <div className="flex-1">
            <Input label="Name" size={InputSize.FULL} value={name} onChange={(e) => setName(e.target.value)} placeholder="Website" required />
          </div>
          <Button primary type="submit" icon={Plus}>
            New project
          </Button>
        </form>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- roles & grants

/** "u:12" / "g:3" — one select for users and groups. */
const granteeOptions = (data: AdminOverview): { value: string; label: string }[] => [
  ...data.groups.map((g) => ({ value: `g:${g.id}`, label: `${g.name} (group)` })),
  ...data.users.filter((u) => u.status !== "disabled").map((u) => ({ value: `u:${u.id}`, label: `${u.name} (${u.email})` })),
];
const granteeInput = (v: string): { userId: number } | { groupId: number } => (v.startsWith("g:") ? { groupId: Number(v.slice(2)) } : { userId: Number(v.slice(2)) });

function GrantTable({ data, run, grants, showProject = false }: { data: AdminOverview; run: Run; grants: AdminOverview["grants"]; showProject?: boolean }) {
  const userName = (uid: number): string => data.users.find((u) => u.id === uid)?.name ?? `user ${uid}`;
  const groupName = (gid: number): string => data.groups.find((g) => g.id === gid)?.name ?? `group ${gid}`;
  const roleName = (rid: number): string => data.roles.find((r) => r.id === rid)?.name ?? `role ${rid}`;
  const projectName = (pid: number | null): string => (pid === null ? "All projects" : (data.projects.find((p) => p.id === pid)?.key ?? `project ${pid}`));
  return (
    <table className="w-full">
      <thead>
        <tr>
          <Th>User / group</Th>
          <Th>Role</Th>
          {showProject && <Th>Scope</Th>}
          <Th />
        </tr>
      </thead>
      <tbody>
        {grants.map((g) => (
          <tr key={g.id}>
            <Td>
              {g.groupId !== null ? (
                <span>
                  {groupName(g.groupId)} <span className="chip ml-1">group</span>
                </span>
              ) : (
                userName(g.userId!)
              )}
            </Td>
            <Td>{roleName(g.roleId)}</Td>
            {showProject && <Td className="font-mono">{projectName(g.projectId)}</Td>}
            <Td>
              <Button icon={X} title="Revoke" onClick={() => void run("revokeRole", { grantId: g.id })} />
            </Td>
          </tr>
        ))}
        {grants.length === 0 && (
          <tr>
            <Td className="text-faint">no roles granted</Td>
            <Td />
            {showProject && <Td />}
            <Td />
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Roles({ data, run }: { data: AdminOverview; run: Run }) {
  const me = useClient((s) => s.me);
  const [name, setName] = useState("");
  const [addWho, setAddWho] = useState("");
  const [addRole, setAddRole] = useState(String(data.roles[0]?.id ?? ""));
  return (
    <div className="max-w-5xl">
      <H
        title="Roles"
        action={
          me?.isAdmin && (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) void run("createRole", { name: name.trim(), permissions: Permission.READ_ISSUE.toString() }).then((ok) => ok && setName(""));
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New role" />
              <Button type="submit" icon={Plus} disabled={!name.trim()}>
                Create
              </Button>
            </form>
          )
        }
      />
      <p className="mb-3 text-xs text-muted">
        A role is a set of permissions. Grant it to users or groups per project (on the project page) or in every project (below). Built-in roles keep their names; their permissions can be changed.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <Th>Permission</Th>
              {data.roles.map((r) => (
                <Th key={r.id}>
                  <RoleHeader role={r} run={run} editable={!!me?.isAdmin} />
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_INFO.map((info, i) => {
              const bit = Permission[info.key];
              const groupStart = i === 0 || PERMISSION_INFO[i - 1]!.group !== info.group;
              return (
                <tr key={info.key} className={groupStart ? "border-t-2 border-line" : ""}>
                  <Td>
                    {groupStart && <div className="mb-0.5 text-xs text-faint uppercase">{info.group}</div>}
                    {info.label}
                  </Td>
                  {data.roles.map((r) => {
                    const bits = BigInt(r.permissions);
                    const on = (bits & bit) === bit;
                    return (
                      <Td key={r.id}>
                        <Checkbox
                          checked={on}
                          disabled={!me?.isAdmin}
                          onChange={(e) => {
                            const next = e.target.checked ? bits | bit : bits & (ALL_PERMISSION_BITS ^ bit);
                            void run("updateRole", { roleId: r.id, permissions: next.toString() });
                          }}
                        />
                      </Td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <section className="mt-8">
        <h2 className="mb-2 text-sm font-medium text-muted uppercase">Global roles</h2>
        <p className="mb-2 text-xs text-muted">Granted in every project, including projects created later. YouTrack's "Registered Users → Observer" lives here.</p>
        <GrantTable data={data} run={run} grants={data.grants.filter((g) => g.projectId === null)} />
        {me?.isAdmin && (
          <form
            className="mt-2 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (addWho) void run("grantRole", { projectId: null, ...granteeInput(addWho), role: Number(addRole) }).then((ok) => ok && setAddWho(""));
            }}
          >
            <SimpleSelect value={addWho} placeholder="Add user or group…" options={granteeOptions(data)} onChange={setAddWho} />
            <SimpleSelect value={addRole} options={data.roles.map((r) => ({ value: String(r.id), label: r.name }))} onChange={setAddRole} />
            <Button type="submit" icon={Plus} disabled={!addWho}>
              Grant globally
            </Button>
          </form>
        )}
      </section>

      <section className="mt-8">
        <h2 className="mb-2 text-sm font-medium text-muted uppercase">All project grants</h2>
        <GrantTable data={data} run={run} grants={data.grants.filter((g) => g.projectId !== null)} showProject />
      </section>
    </div>
  );
}

function RoleHeader({ role: r, run, editable }: { role: AdminRole; run: Run; editable: boolean }) {
  return (
    <div className="flex min-w-28 flex-col gap-1 normal-case">
      {r.builtin || !editable ? (
        <span className="font-medium text-ink">{r.name}</span>
      ) : (
        <Input
          inputClassName="w-28"
          defaultValue={r.name}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== r.name) void run("updateRole", { roleId: r.id, name: v });
          }}
        />
      )}
      {r.builtin ? (
        <span className="text-[10px] font-normal text-faint">built-in</span>
      ) : (
        editable && (
          <Button
            inline
            icon={Trash2}
            title="Delete role (revokes it everywhere)"
            onClick={() => {
              if (window.confirm(`Delete role "${r.name}"? Every grant of it is revoked.`)) void run("deleteRole", { roleId: r.id });
            }}
          />
        )
      )}
    </div>
  );
}

// ----------------------------------------------------------------- fields & bundles

const KINDS: FieldKind[] = ["enum", "state", "user", "version", "build", "owned", "date", "datetime", "period", "integer", "float", "string", "text"];
const BUNDLE_KIND: Partial<Record<FieldKind, string>> = { enum: "enum", state: "state", version: "version", build: "build", owned: "owned" };

function Fields({ data, run }: { data: AdminOverview; run: Run }) {
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<FieldKind>("enum");
  const [multi, setMulti] = useState(false);
  const [bundleId, setBundleId] = useState("");
  const [newBundle, setNewBundle] = useState("");
  if (!schema) return null;
  const needsBundle = BUNDLE_KIND[kind] !== undefined;
  const bundlesFor = data.bundles.filter((b) => b.kind === BUNDLE_KIND[kind]);
  const create = async (): Promise<void> => {
    let bid: number | null = null;
    if (needsBundle) {
      if (bundleId === "new" || bundlesFor.length === 0) {
        const bname = newBundle.trim() || `${name.trim()} values`;
        try {
          bid = (await client.http.command<{ id: number }>("createBundle", { name: bname, kind: BUNDLE_KIND[kind] }, uuid())).id;
        } catch (e) {
          client.pushError(e instanceof ApiError ? e.code : "network", (e as Error).message);
          return;
        }
      } else bid = Number(bundleId);
    }
    if (await run("createCustomField", { name: name.trim(), kind, multi, bundleId: bid })) {
      setName("");
      setNewBundle("");
    }
  };
  return (
    <div className="max-w-4xl">
      <H title="Custom Fields" />
      <table className="w-full">
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Kind</Th>
            <Th>Option set</Th>
            <Th>Used in</Th>
          </tr>
        </thead>
        <tbody>
          {schema.data.fields.map((f) => (
            <tr key={f.id}>
              <Td>
                {me?.isAdmin ? (
                  <Input
                    inputClassName="w-48"
                    defaultValue={f.name}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== f.name) void run("updateCustomField", { fieldId: f.id, name: v });
                    }}
                  />
                ) : (
                  f.name
                )}
              </Td>
              <Td className="text-muted">{f.kind}{f.multi ? " (multi)" : ""}</Td>
              <Td>{f.bundleId !== undefined ? data.bundles.find((b) => b.id === f.bundleId)?.name : <span className="text-faint">—</span>}</Td>
              <Td className="text-muted">{schema.data.projectFields.filter((pf) => pf.fieldId === f.id).map((pf) => schema.projectsById.get(pf.projectId)?.key ?? pf.projectId).join(", ") || "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>

      {me?.isAdmin && (
        <form
          className="mt-4 flex flex-wrap items-end gap-2 rounded border border-line bg-surface p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          <label className="block">
            <span className="mb-1 block text-xs text-muted">Kind</span>
            <SimpleSelect value={kind} options={KINDS.map((k) => ({ value: k, label: k }))} onChange={(v) => setKind(v as FieldKind)} />
          </label>
          <Checkbox label="multiple values" containerClassName="pb-1.5" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
          {needsBundle && (
            <>
              <label className="block">
                <span className="mb-1 block text-xs text-muted">Option set</span>
                <SimpleSelect value={bundlesFor.length ? bundleId || "new" : "new"} options={[{ value: "new", label: "New option set…" }, ...bundlesFor.map((b) => ({ value: String(b.id), label: b.name }))]} onChange={setBundleId} />
              </label>
              {(bundleId === "new" || bundleId === "" || bundlesFor.length === 0) && <Input label="New set name" value={newBundle} onChange={(e) => setNewBundle(e.target.value)} placeholder={`${name || "Field"} values`} />}
            </>
          )}
          <Button primary type="submit" icon={Plus}>
            Create field
          </Button>
        </form>
      )}

      <h2 className="mt-10 mb-3 text-lg font-semibold tracking-tight">Option sets</h2>
      <div className="flex flex-col gap-6">
        {data.bundles.map((b) => (
          <Bundle key={b.id} bundle={b} run={run} editable={me?.isAdmin ?? false} />
        ))}
      </div>
    </div>
  );
}

function Bundle({ bundle: b, run, editable }: { bundle: AdminBundle; run: Run; editable: boolean }) {
  const [adding, setAdding] = useState("");
  const move = (i: number, d: number): void => {
    const ids = b.elements.map((e) => e.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    void run("reorderBundleElements", { bundleId: b.id, elementIds: ids });
  };
  return (
    <div className="rounded border border-line bg-surface p-3">
      <div className="mb-2 flex items-center gap-2">
        <b>{b.name}</b>
        <span className="text-muted">{b.kind}</span>
      </div>
      <table className="w-full">
        <thead>
          <tr>
            <Th>Value</Th>
            <Th>Colour</Th>
            {b.kind === "state" && <Th>Resolved</Th>}
            <Th>Archived</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {b.elements.map((el, i) => (
            <tr key={el.id} className={el.archived ? "text-muted" : ""}>
              <Td>
                <Input
                  inputClassName="w-44"
                  defaultValue={el.name}
                  disabled={!editable}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== el.name) void run("updateBundleElement", { elementId: el.id, name: v });
                  }}
                />
              </Td>
              <Td>
                <span className="flex items-center gap-1">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((c) => (
                    <button
                      key={c}
                      className={`h-5 w-5 rounded ${c > 0 ? `chip-${c}` : ""} ${(el.colorId ?? 0) === c ? "ring-2 ring-focus" : ""}`}
                      style={{ background: "var(--chip-bg, var(--sunken))" }}
                      title={c === 0 ? "No colour" : `Colour ${c}`}
                      disabled={!editable}
                      onClick={() => void run("updateBundleElement", { elementId: el.id, colorId: c === 0 ? null : c })}
                    />
                  ))}
                </span>
              </Td>
              {b.kind === "state" && (
                <Td>
                  <Checkbox checked={el.isResolved} disabled={!editable} onChange={(e) => void run("updateBundleElement", { elementId: el.id, isResolved: e.target.checked })} />
                </Td>
              )}
              <Td>
                <Checkbox checked={el.archived} disabled={!editable} onChange={(e) => void run("updateBundleElement", { elementId: el.id, archived: e.target.checked })} />
              </Td>
              <Td>
                {editable && (
                  <span className="flex">
                    <Button icon={ArrowUp} onClick={() => move(i, -1)} title="Up" />
                    <Button icon={ArrowDown} onClick={() => move(i, 1)} title="Down" />
                  </span>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable && (
        <form
          className="mt-2 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (adding.trim()) void run("addBundleElement", { bundleId: b.id, name: adding.trim(), isResolved: false, colorId: null }).then((ok) => ok && setAdding(""));
          }}
        >
          <Input value={adding} onChange={(e) => setAdding(e.target.value)} placeholder="New value" />
          <Button type="submit" icon={Plus} disabled={!adding.trim()}>
            Add
          </Button>
        </form>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- users & groups

function Users({ data, run }: { data: AdminOverview; run: Run }) {
  const me = useClient((s) => s.me);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [invited, setInvited] = useState<string | null>(null);
  const invite = async (): Promise<void> => {
    if (await run("inviteUser", { email: email.trim(), name: name.trim() })) {
      setInvited(`Invitation created for ${email.trim()}. The link was sent by email (or printed on the server console in development).`);
      setEmail("");
      setName("");
    }
  };
  return (
    <div className="max-w-4xl">
      <H title="Users" />
      <table className="w-full">
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Email</Th>
            <Th>Status</Th>
            <Th>Admin</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {data.users.map((u) => (
            <tr key={u.id} className={u.status === "disabled" ? "text-muted" : ""}>
              <Td>
                <Input
                  inputClassName="w-44"
                  defaultValue={u.name}
                  disabled={!me?.isAdmin && me?.id !== u.id}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== u.name) void run("updateUser", { userId: u.id, name: v });
                  }}
                />
              </Td>
              <Td>{u.email}</Td>
              <Td>
                <span className={`chip ${u.status === "active" ? "chip-2" : u.status === "invited" ? "chip-4" : ""}`}>{u.status}</span>
              </Td>
              <Td>
                <Checkbox checked={u.isAdmin} disabled={!me?.isAdmin || u.id === me.id} onChange={(e) => void run("setAdmin", { userId: u.id, isAdmin: e.target.checked })} />
              </Td>
              <Td>
                {me?.isAdmin && u.id !== me.id && u.status !== "invited" && (
                  <Button inline onClick={() => void run("setUserStatus", { userId: u.id, status: u.status === "disabled" ? "active" : "disabled" })}>
                    {u.status === "disabled" ? "Enable" : "Disable"}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      {me?.isAdmin && (
        <form
          className="mt-6 flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void invite();
          }}
        >
          <div className="flex-1">
            <Input label="Email" size={InputSize.FULL} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="flex-1">
            <Input label="Name" size={InputSize.FULL} value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <Button primary type="submit" icon={Plus}>
            Invite
          </Button>
        </form>
      )}
      {invited && <div className="mt-3 rounded bg-accent-soft px-3 py-2">{invited}</div>}
    </div>
  );
}

function Groups({ data, run }: { data: AdminOverview; run: Run }) {
  const me = useClient((s) => s.me);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState<Record<number, string>>({});
  const userName = (uid: number): string => data.users.find((u) => u.id === uid)?.name ?? `user ${uid}`;
  return (
    <div className="max-w-4xl">
      <H
        title="Groups"
        action={
          me?.isAdmin && (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) void run("createGroup", { name: name.trim() }).then((ok) => ok && setName(""));
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New group" />
              <Button type="submit" icon={Plus} disabled={!name.trim()}>
                Create
              </Button>
            </form>
          )
        }
      />
      {data.groups.length === 0 && <p className="text-muted">No groups yet. Groups are used for issue and comment visibility, and can hold roles (see Roles and each project).</p>}
      <div className="flex flex-col gap-4">
        {data.groups.map((g) => (
          <div key={g.id} className="rounded border border-line bg-surface p-3">
            <b>{g.name}</b>
            <ul className="mt-2 flex flex-wrap gap-2">
              {g.members.map((uid) => (
                <li key={uid}>
                  <Tag readOnly={!me?.isAdmin} {...(me?.isAdmin ? { onRemove: () => void run("removeFromGroup", { groupId: g.id, userId: uid }) } : {})}>
                    {userName(uid)}
                  </Tag>
                </li>
              ))}
              {g.members.length === 0 && <li className="text-faint">no members</li>}
            </ul>
            {me?.isAdmin && (
              <form
                className="mt-2 flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  const uid = adding[g.id];
                  if (uid) void run("addToGroup", { groupId: g.id, userId: Number(uid) }).then((ok) => ok && setAdding((a) => ({ ...a, [g.id]: "" })));
                }}
              >
                <SimpleSelect value={adding[g.id] ?? ""} placeholder="Add user…" options={data.users.filter((u) => u.status === "active" && !g.members.includes(u.id)).map((u) => ({ value: String(u.id), label: u.name }))} onChange={(v) => setAdding((a) => ({ ...a, [g.id]: v }))} />
                <Button type="submit" icon={Plus} disabled={!adding[g.id]}>
                  Add
                </Button>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

